import React, { useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import { BackIcon, UploadIcon } from "../../../components/Dashboard/DataCenterIcons";
import { ROUTES } from "../../../utils/constants";
import "../../../assets/styles/FDCAppendReuploadPage.css";
import commonService from "../../../services/commonService";

interface LocationState {
  FileId: string;
  actionType: "append" | "reupload";
  stage?: string;
}

interface UploadState {
  file: File | null;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  success: boolean;
  uploadedFileName: string | null;
}

const FDCAppendReuploadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { FileId, actionType, stage } = state || {};
  // A delivered project's 5-day grace-window replace needs a reason on
  // record (enforced server-side) - only "reupload" (replace) ever needs
  // one, never "append".
  const isDelivered = stage === "delivered";
  const showReasonField = actionType === "reupload";

  const [uploadState, setUploadState] = useState<UploadState>({
    file: null,
    isUploading: false,
    uploadProgress: 0,
    error: null,
    success: false,
    uploadedFileName: null,
  });
  const [reason, setReason] = useState("");

  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File): string | null => {
    // Validate file size (e.g., 10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return "File size exceeds 10MB limit";
    }

    // Validate file type (only CSV)
    const allowedExtensions = ['.csv'];
    const fileName = file.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidExtension) {
      return "Please upload CSV files only";
    }

    return null;
  };

  const processFile = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadState(prev => ({
        ...prev,
        error
      }));
      return;
    }

    setUploadState({
      file,
      isUploading: false,
      uploadProgress: 0,
      error: null,
      success: false,
      uploadedFileName: null,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    processFile(e.target.files[0]);
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!e.dataTransfer.files?.length) return;
    
    const file = e.dataTransfer.files[0];
    processFile(file);
  }, []);

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const simulateUploadProgress = () => {
    return new Promise<void>((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadState(prev => ({ ...prev, uploadProgress: progress }));
        
        if (progress >= 100) {
          clearInterval(interval);
          resolve();
        }
      }, 200);
    });
  };

  const handleUpload = async () => {
    if (!uploadState.file || !FileId) return;

    setUploadState(prev => ({ 
      ...prev, 
      isUploading: true, 
      error: null,
      success: false 
    }));

    try {
      // Simulate upload progress
      await simulateUploadProgress();

      const formData = new FormData();
      formData.append("file", uploadState.file);
      formData.append("file_id", FileId);
      if (reason.trim()) {
        formData.append("reason", reason.trim());
      }

      const response = await commonService.fdcAppendOrReplace(formData, actionType);
      const newFileName = response?.data?.file_name || uploadState.file.name;

      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        success: true,
        uploadProgress: 100,
        uploadedFileName: newFileName,
      }));

      // Show success message
      setTimeout(() => {
        navigate(ROUTES.FDC_LIST);
      }, 2000);

    } catch (error: any) {
      console.error("Upload failed:", error);
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: (error?.response?.data?.message || error?.response?.message) || "Upload failed. Please try again."
      }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleCancel = () => {
    setUploadState({
      file: null,
      isUploading: false,
      uploadProgress: 0,
      error: null,
      success: false,
      uploadedFileName: null,
    });
    setReason("");
  };

  return (
    <MainLayout>
      <div className="fdc-append-reupload-page">
        {/* Header Section */}
        <div className="fdc-header">
          <button 
            onClick={() => navigate(ROUTES.FDC_LIST)} 
            className="fdc-back-button"
          >
            <BackIcon />
            <span>Back</span>
          </button>      
          
        </div>

        {/* Upload Section */}
        <div className="upload-container">
          <div className="upload-card">
            <div className="upload-header">
              <h2>Select File to {actionType === "append" ? "Append" : "Replace"}</h2>
              <p className="upload-instructions">
                {actionType === "append" 
                  ? "Choose a file to append to the existing document"
                  : "Choose a file to replace the existing document"}
              </p>
            </div>

            {/* File Input Area with Drag & Drop */}
            <div 
              className={`file-input-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClickUpload}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                className="file-input"
                onChange={handleFileChange}
                disabled={uploadState.isUploading}
                style={{ display: "none" }}
                accept=".csv"
              />
              
              <div className="file-input-label">
                <div className="upload-placeholder">
                  <UploadIcon />
                  <p className="upload-text">
                    {uploadState.file 
                      ? uploadState.file.name 
                      : "Click to browse or drag and drop"}
                  </p>
                  <p className="upload-hint">
                    Supports CSV files • Max 10MB
                  </p>
                  {/* {isDragging && (
                    <div className="drag-overlay">
                      Drop your CSV file here
                    </div>
                  )} */}
                </div>
              </div>
            </div>

            {/* Selected File Preview */}
            {uploadState.file && !uploadState.isUploading && (
              <div className="selected-file-card">
                <div className="file-info">
                  <span className="file-name">{uploadState.file.name}</span>
                  <span className="file-size" style={{ textAlign:'left'}}>
                    {formatFileSize(uploadState.file.size)}
                  </span>
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  className="remove-file-btn"
                  disabled={uploadState.isUploading}
                >
                  ×
                </button>
              </div>
            )}

            {/* Reason - only for Re-Upload (replace); required once the
                project is already delivered, matching the backend's 5-day
                grace-window rule. */}
            {showReasonField && uploadState.file && !uploadState.isUploading && (
              <div className="reupload-reason-field" style={{ marginTop: 16 }}>
                <label htmlFor="reupload-reason" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Reason{isDelivered ? " *" : " (optional)"}
                </label>
                <textarea
                  id="reupload-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={isDelivered
                    ? "Required - why is this delivered project's file being replaced?"
                    : "Why is this file being replaced? (optional)"}
                  rows={3}
                  style={{ width: "100%", resize: "vertical" }}
                />
              </div>
            )}

            {/* Progress Bar */}
            {uploadState.isUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${uploadState.uploadProgress}%` }}
                  />
                </div>
                <span className="progress-text">
                  {uploadState.uploadProgress}%
                </span>
              </div>
            )}

            {/* Error Message */}
            {uploadState.error && (
              <div className="error-message">
                ⚠️ {uploadState.error}
              </div>
            )}

            {/* Success Message */}
            {uploadState.success && (
              <div className="success-message">
                ✅ {uploadState.uploadedFileName ? `"${uploadState.uploadedFileName}" uploaded successfully!` : "File uploaded successfully!"} Redirecting...
              </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                onClick={handleCancel}
                className="btn-secondary"
                disabled={uploadState.isUploading || !uploadState.file}
              >
                Clear
              </button>
              
              <button
                onClick={handleUpload}
                className="btn-primary"
                disabled={!uploadState.file || uploadState.isUploading || uploadState.success || (isDelivered && showReasonField && !reason.trim())}
              >
                {uploadState.isUploading ? (
                  <>
                    <span className="spinner"></span>
                    Uploading...
                  </>
                ) : (
                  `${actionType === "append" ? "Append" : "Re-Upload"} File`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FDCAppendReuploadPage;